import React, {createContext, useContext, useEffect, useState, useRef} from 'react';
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import {useParams} from "react-router";
import useDidMountEffect from "../hooks/useDidMountEffect";
import {useDebounce, usePrevious} from "@uidotdev/usehooks";
import useEnterEffect from "../hooks/useEnterEffect";
import {useUserContext} from "./UserContext";
import {toast} from "react-toastify";
import {DeskingModal} from "../pages/PricingListPage/DeskingModal";
import {ListingsPageModal} from "../pages/ListingsPage/ListingsPageModal";
import {useModalsStore} from "../stores/modalsStore";
import {PAYMENT_ADJUSTMENT} from "../components/PaymentAdjustmentPopover";


export const PURCHASE_DEAL_LIGHT = '#1BA897'
export const PURCHASE_DEAL_DARK = '#007D6E'
export const LEASE_DEAL_DARK = '#2267CE'
// export const LEASE_DEAL_LIGHT = '#2B8EEA'
export const LEASE_DEAL_LIGHT = '#2267CE'

export const DealContext = createContext(undefined)

/**
 * Backend integration boundary for the Payment Adjustment popover: the fields a
 * confirmed choice adds to the update request, or null when there is no
 * request to send.
 *
 * TDO is the existing roll — desired_payment with drive_off cleared adjusts the
 * CustomerCash. Cost and Profit roll the selling price instead and the backend
 * keeps the Amount Due at Signing, so drive_off is left as it is. An empty
 * payment, whatever the choice, returns the deal to its normal Amount Due at
 * Signing (the old "clear Monthly Payment" behavior).
 */
export const paymentAdjustmentRequest = (choice, payment) => {
    if (payment === '' || payment == null) {
        return {desired_payment: null, drive_off: null}
    }
    if (choice === PAYMENT_ADJUSTMENT.DRIVE_OFF) {
        return {desired_payment: payment, drive_off: null}
    }
    if (choice === PAYMENT_ADJUSTMENT.COST || choice === PAYMENT_ADJUSTMENT.PROFIT) {
        return {desired_payment: payment, payment_adjustment: choice}
    }
    return null
}

const getInitialState = (location) => {
    return {
        msrp: '',
        cost: '',
        description: '',
        invoice: 0,
        above: 0,
        back: 0,
        profit: 0,
        car: null,
        is_purchase: false,
        location: location,
        location_id: location.id,
        drive_off: null,
        term: 36,
        mileage: 10_000,

        address_line_one: location.address_line_one,
        address_line_two: location.address_line_two,
        city: location.city,
        state: location.state,
        zipcode: location.zipcode,
        county: location.county,
    }
}

export const DealContextProvider = ( { children, id, isDeskingContext, ...props } ) => {
    const { profileState, isFeatureFlagEnabled, canDesk } = useUserContext()
    const openModal = useModalsStore((state) => state.openModal)
    const params = useParams();

    // Historical Current Vehicle mode. `currentVehicle` (the WON opportunity's
    // inception/maturity dates) is the only thing that turns it on, so a normal
    // quote — which never passes it — is untouched.
    //
    // The guards below are the read-only GUARANTEE, not the affordance: every
    // write in the DealPage tree funnels through these functions, so a control
    // we forgot to disable (or one added later) still cannot reach the backend.
    // Components disable their own inputs on top of this so the page also
    // *reads* read-only.
    const isHistorical = Boolean(props.currentVehicle)

    const dealId = () => {
        return id || params.id
    }

    // The CRM person whose opportunity holds this quote, for the header's back
    // link. Asked alongside the deal fetch so the link arrives with the quote;
    // only one unambiguous, visible person counts, and a failure stays silent.
    const [crmPerson, setCrmPerson] = useState(null)
    const crmQuoteId = dealId()
    const isCrmLookupEnabled = !isHistorical && !isDeskingContext && isFeatureFlagEnabled('pipeline')

    useEffect(function loadCrmPersonForQuote(){
        setCrmPerson(null)
        if (!isCrmLookupEnabled || !crmQuoteId) return
        let cancelled = false
        fetcher.getCrmOpportunities(`quote=${crmQuoteId}`)
            .then((res) => {
                const people = (res?.data?.results || [])
                    .map((opportunity) => opportunity?.person_detail)
                    .filter((person) => person?.id)
                const isUnambiguous = new Set(people.map((person) => person.id)).size === 1
                if (!cancelled) setCrmPerson(isUnambiguous ? people[0] : null)
            })
            .catch(() => {})
        return () => { cancelled = true }
    }, [crmQuoteId, isCrmLookupEnabled])

    const [dealState, setDealState] = useState(null)

    const dealPageRef = useRef(null)

    const [createDealResponse, createDeal] = useFetchHook(fetcher.createDeal)
    const [cloneDealResponse, cloneDeal] = useFetchHook(fetcher.createDeal)
    const [fetchDealResponse, fetchDeal] = useFetchHook(fetcher.getDeal)
    const [updateDealResponse, updateDeal] = useFetchHook(fetcher.updateDeal)
    const [getDeskingResponse, fetchDesking] = useFetchHook(fetcher.getDesking)

    const [updateDealWithoutQuoteUpdateResponse, updateDealWithoutQuoteUpdate] = useFetchHook(fetcher.updateDeal)

    const debouncedDeal = useDebounce(dealState, 400)

    const prevState = usePrevious(dealState);

    // The last backend-accepted (successfully priced) deal. We revert to THIS on a
    // failed re-price — not `prevState` ("one render ago"), which the per-keystroke
    // term/mileage commits pollute with half-typed values (e.g. the "2" of "23").
    const lastGoodStateRef = useRef(null);

    const dealColorDark = () => {
        return dealState.is_purchase ? PURCHASE_DEAL_DARK : LEASE_DEAL_DARK
    }

    const dealColorLight = () => {
        return dealState.is_purchase ? PURCHASE_DEAL_LIGHT : LEASE_DEAL_DARK
    }

    const dealColorDarkAlt = () => {
        return dealState.is_purchase ? LEASE_DEAL_DARK : PURCHASE_DEAL_DARK
    }

    const dealColorLightAlt = () => {
        return dealState.is_purchase ? LEASE_DEAL_DARK : PURCHASE_DEAL_LIGHT
    }

    const setDealStateWithUpdate = (newState, shouldUpdate = false) => {
        if (isHistorical) return
        setDealState({...dealState, ...newState, _updateInBackend: shouldUpdate })
    }

    const setDealStateWithoutUpdate = (newState) => {
        setDealState(newState)
    }

    const setDealStateWithoutQuoteUpdate = (newState) => {
        if (isHistorical) return
        return updateDealWithoutQuoteUpdate(dealState.id, {...newState, _no_update: true})
    }

    const updateDealFunc = (state = dealState) => {
        if (isHistorical) return
        // Partner-API quotes can't be re-priced in place — they need a fresh desk,
        // so open the Desking modal. Expired quotes are updated the regular way
        // (the backend re-prices and reports if the program is still expired).
        if (state.formatted_quote.source === 'PARTNER_API') {
            openModal({
                id: "DeskingModal",
                element: DeskingModal,
                props: {
                    id: state.id,
                    deal_id: state.id,
                    ...state,
                    program_code: state.formatted_quote?.program_code,
                    onSave: (updateData) => setDealStateWithUpdate(updateData, false),
                }
            })
            return
        }
        const updateObj = structuredClone(state)
        delete updateObj._updateInBackend
        // The selected program lives on the quote; lift its code to the top level
        // so the backend re-prices against the same program on update.
        updateObj.program_code = state.formatted_quote?.program_code
        return updateDeal(state.id, updateObj)
    }

    // Calculates a payment entered in the Payment Adjustment popover. The payment
    // only ever lives in the popover, so auto-saves can't carry it; nothing is
    // sent mid-calculation.
    const submitPaymentAdjustment = (payment, choice) => {
        if (isHistorical || updateDealResponse?.loading) return
        const fields = paymentAdjustmentRequest(choice, payment)
        if (!fields) return

        // payment_adjustment rides this request only: kept in dealState, every
        // later save would re-send it and roll again.
        const {payment_adjustment, ...stateFields} = fields
        // This request supersedes any queued auto-save.
        const nextState = {...dealState, ...stateFields, _updateInBackend: false}
        setDealStateWithoutUpdate(nextState)
        return updateDealFunc(payment_adjustment ? {...nextState, payment_adjustment} : nextState)
    }

    const createUpdateFunc = () => {
        if (isHistorical) return
        const updateObj = structuredClone(dealState)

        delete updateObj._updateInBackend
        // Lift the selected program's code to the top level so the backend
        // prices the new deal against the same program (matches updateDealFunc).
        updateObj.program_code = dealState.formatted_quote?.program_code
        if (updateObj.id) {
            return updateDealFunc()
        } else {
            return createDeal(updateObj).then((data) => {
                openModal({
                    id: "DeskingModal",
                    element: DeskingModal,
                    props: {
                        id: data.data.id,
                        deal_id: data.data.id
                    }
                })
            })
        }
    }

    const resetDealState = () => {
        setDealState(getInitialState(profileState.default_location))
    }

    // Open the full ListingsPage as a modal (instead of a new tab), seeded by this
    // deal's vehicle. Selecting a listing writes its combined MSRP + VIN back onto
    // the deal (one update → reprices on the new MSRP and persists the VIN).
    const openListingsModal = () => {
        if (isHistorical) return
        const car = dealState?.car_detail || {}
        openModal({
            element: ListingsPageModal,
            id: "ListingsPageModal",
            props: {
                seed: {
                    // Model-level search — no trim.
                    year: car.year,
                    make: car.make,
                    model: car.model,
                    zipcode: dealState?.dealer_zipcode || undefined,
                    // Preselect New/Used to match the deal's vehicle.
                    inventory_type: car.is_new ? 'new' : 'used',   // passed through as seed → inventory_types
                },
                onSelectListing: (listing) => {
                    const hasMsrp = listing?.msrp != null && listing.msrp !== ''
                    const vin = listing?.vin || null
                    if (!hasMsrp && !vin) return

                    // Merge onto the deal and reflect it in the UI immediately.
                    const merged = {...dealState}
                    if (hasMsrp) merged.msrp = listing.msrp
                    if (vin) merged.vin = vin
                    setDealStateWithoutUpdate(merged)

                    // Persist right away (no debounce): reprice on the new MSRP for
                    // a normal quote, or persist without a reprice for partner-API
                    // quotes (which can't be re-priced in place).
                    if (merged.formatted_quote?.source === 'PARTNER_API') {
                        setDealStateWithoutQuoteUpdate(merged)
                    } else {
                        const payload = structuredClone(merged)
                        delete payload._updateInBackend
                        payload.program_code = merged.formatted_quote?.program_code
                        updateDeal(merged.id, payload)
                    }
                },
            },
        })
    }

    useEnterEffect(function updateDealOnEnter(){
        // A historical vehicle is a record, not a worksheet — Enter must not
        // re-price it, the same reason the Calculate button isn't rendered.
        if (isHistorical) return
        // Same for a role that may not desk: the button is gone, so the
        // shortcut has to go with it or the restriction is cosmetic.
        if (!canDesk()) return
        // The listener is on `document` and registers before the deal has been
        // fetched, so an Enter in that window reached a null dealState.
        if (!dealState) return
        if (dealState.base_car && dealState.msrp && dealState.cost && !updateDealResponse.loading) {
            if (!dealState.is_locked) {
                createUpdateFunc()
            } else {
                setDealStateWithoutUpdate(prevState)
                toast.warn("This deal is locked and cannot be updated.")
            }

        }
    }, [dealState, updateDealResponse])

    useEffect(function fetchDealUsingID(){
        if (dealId()) {
            if (isDeskingContext) {
                fetchDesking(dealId())
            } else {
                fetchDeal(dealId())
            }
        } else {
            setDealState(getInitialState(profileState.default_location))
        }
    }, [dealId()])

    useEffect(function setFetchedQuote(){
        if (fetchDealResponse?.data?.data) {
            lastGoodStateRef.current = fetchDealResponse?.data?.data
            setDealStateWithoutUpdate(fetchDealResponse?.data?.data)
        }
    }, [fetchDealResponse])

    useEffect(function setCreatedQuote(){
        if (createDealResponse?.data?.data) {
            lastGoodStateRef.current = createDealResponse?.data?.data
            setDealStateWithoutUpdate(createDealResponse?.data?.data)
        }
    }, [createDealResponse])

    useDidMountEffect(function updateQuoteInBackend(){
        if (isHistorical) return
        if (dealState?._updateInBackend === true) {
            updateDealFunc()
        }
    }, [debouncedDeal])

    useEffect(function returnToPreviousState() {
        if (updateDealResponse?.error && !updateDealResponse.loading) {
            // Revert to the last successfully-priced deal (falls back to prevState
            // if we somehow never priced one). This restores the original term and
            // its offers rather than a half-typed intermediate value.
            setDealStateWithoutUpdate(lastGoodStateRef.current ?? prevState)
        }
    }, [updateDealResponse])

    useDidMountEffect(function updateQuoteFromBackend(){
        if (updateDealResponse?.data?.data && !updateDealResponse.loading && !updateDealResponse.error) {
            const newDealState = updateDealResponse?.data?.data
            lastGoodStateRef.current = newDealState
            setDealStateWithoutUpdate(newDealState)

            const expirationDate = newDealState.formatted_quote.expiration_date
            const isExpired = new Date(expirationDate).setUTCHours(0,0,0,0) <= new Date().setUTCHours(0,0,0,0)

            if (isExpired) {
                toast.warn("This program is expired.")
            } else {
                toast.success("Successfully updated quote.")
            }

            props.onSave && props.onSave(newDealState)
        }
    }, [updateDealResponse])

    useDidMountEffect(function updateDealFromBackendWhenNotUpdatingQuote(){
        if (updateDealWithoutQuoteUpdateResponse?.data?.data && !updateDealWithoutQuoteUpdateResponse.loading && !updateDealWithoutQuoteUpdateResponse.error) {
            const newDealState = updateDealWithoutQuoteUpdateResponse?.data?.data
            setDealStateWithoutUpdate(newDealState)

            const expirationDate = newDealState.formatted_quote.expiration_date
            const isExpired = new Date(expirationDate).setUTCHours(0,0,0,0) <= new Date().setUTCHours(0,0,0,0)

            if (isExpired) {
                toast.warn("This program is expired.")
            } else {
                toast.success("Successfully updated quote.")
            }

            props.onSave && props.onSave(newDealState)
        }
    }, [updateDealWithoutQuoteUpdateResponse])

    const value = {
        dealPageRef,
        dealState,

        createDeal,
        createDealResponse,

        updateDeal,
        updateDealResponse,

        fetchDeal,
        fetchDealResponse,

        cloneDeal,
        cloneDealResponse,

        dealId,
        setDealStateWithUpdate,
        setDealStateWithoutUpdate,
        setDealStateWithoutQuoteUpdate,

        dealColorDark,
        dealColorLight,
        dealColorDarkAlt,
        dealColorLightAlt,

        createUpdateFunc,
        updateDealFunc,
        resetDealState,
        submitPaymentAdjustment,
        openListingsModal,

        // The one flag every section reads to render itself read-only.
        isHistorical,
        // The CRM person behind this quote, for the header's back link.
        crmPerson,
        ...props
    }

    if (!isDeskingContext) {
        if (!dealState) return null;

        if (dealId() && !dealState.id) return null;
    }

    return (
        <DealContext.Provider value={value}>
            { children }
        </DealContext.Provider>
    )
}

export class NoDealContextError extends Error {
    constructor(message) {
        super(message);
        this.name = 'NoDealContextError';
    }
}

export const useDealContext = () => {
    const context = useContext(DealContext)

    if (context === undefined) {
        throw new NoDealContextError('useDealContext must be used within DealContext')
    }
    return context
}